import { useState, useEffect, useContext } from 'react';
import { makeStyles } from '@material-ui/styles';
import { Grid, FormLabel, FormGroup, TextField, Button, FilledInput, FormControl, Box } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import XLSX from 'xlsx';

import Loader from '../../common/loader';
import ViewTitle from '../../common/view-title';
import DownloadExcel from '../../common/download-excel';
import Avoided from '../components/avoided';

import AuthContext from '../../context';
import helpers from '../../utils/helpers';
import { get, post } from '../../utils/api-services';

const useStyles = makeStyles(() => ({
    root: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column'
    },
    buttonsContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'flex-end',
        marginBottom: 20
    },
    buttonContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
    },
    search: {
        backgroundColor: '#0d6efd',
        borderColor: '#0d6efd',
        '&:hover': {
            backgroundColor: '#0d6efd',
            borderColor: '#0d6efd',
            boxShadow: 'none',
        }
    }
}));

const defCC1 = { id: 0, name: 'All CC1' }
const defCC2 = { id: 0, name: 'All CC2' }
const defCC3 = { id: 0, name: 'All CC3' }

const ReportEmployees = () => {

    const { showToast, signOut, showLoader } = useContext(AuthContext);
    const classes = useStyles();
    const [open, setOpen] = useState(false);
    const [state, setState] = useState({
        loading: true,
        CC1: [],
        CC2: [],
        CC3: [],
        filter: {
            cc1: defCC1,
            cc2: defCC2,
            cc3: defCC3
        },
        data: [],
        fileDataStore: [],
        fileDataUpdate: [],
        rejected: []
    });

    useEffect(() => {
        makeRequest();
    }, []);

    useEffect(() => {
        if (state.rejected?.length > 0) {
            setOpen(true);
        }
    }, [state.rejected]);

    useEffect(() => {
        setState(prev => ({ ...prev, data: [] }));
    }, [state.filter]);

    const makeRequest = async () => {
        showLoader(true);
        const response = await get('/reports/indexEmployees', state.filter);
        if (response.status === 200) {
            setState(prev => ({
                ...prev,
                loading: false,
                CC1: response?.cc1 ?? [],
                CC2: response?.cc2 ?? [],
                CC3: response?.cc3 ?? []
            }));
        } else {
            console.log('[ReportEmployees] makeRequest error', response);
            helpers.failRequest(response, showToast, signOut);
        }
        showLoader(false);
    }

    const handleChangeAutocomplete = (key, value) => setState(prev => ({ ...prev, filter: { ...prev.filter, [key]: value } }));

    const search = async () => {
        showLoader(true);
        const response = await get('/reports/searchEmployees', state.filter);
        if (response.status === 200) {
            const data = response?.data ?? [];
            setState(prev => ({ ...prev, data }));

            if (!response?.data[0]) showToast('There are no data with these filters', 'warning');
        } else {
            console.log('[ReportEmployees] search error', response);
            helpers.failRequest(response, showToast, signOut);
        }
        showLoader(false);
    }

    const handleFile = async ({ target }) => {
        showLoader(true);

        try {
            if (target.files?.length) {
                const file = target.files[0];
                const buffer = await file.arrayBuffer();
                const unit8 = new Uint8Array(buffer);
                const workbook = XLSX.read(unit8, { type: 'array' });

                const firstSheetName = workbook.SheetNames[0]
                /* Get worksheet */
                const worksheet = workbook.Sheets[firstSheetName]
                const info = XLSX.utils.sheet_to_json(worksheet, { raw: true });
                const data = [];
                info.map(item => {
                    if (item['Code']) {
                        data.push(item);
                    }

                    return false;
                });

                console.log(data);
                if (data.length < 1) {
                    showToast('File format not supported', 'error');
                    target.value = null;
                    clear();
                }

                setState(prev => ({ ...prev, [target.name]: data, rejected: [] }));
            }
        } catch (e) {
            console.log(e);
            showToast('Something went wrong', 'error');
        }

        showLoader(false);
    }

    const store = async () => {
        showLoader(true);

        const response = await post('/employee/importDataStore', { fileData: state.fileDataStore });
        if (response.status === 200) {
            showToast(response.message);
            clear();
        } else {
            clear();
            setState(prev => ({ ...prev, rejected: response?.rejected ?? [] }));

            console.log('[ReportEmployees] store error', response);
            helpers.failRequest(response, showToast, signOut);
        }

        showLoader(false);
    }

    const update = async () => {
        showLoader(true);

        const response = await post('/employee/importDataUpdate', { fileData: state.fileDataUpdate });
        if (response.status === 200) {
            showToast(response.message);
            clear();
        } else {
            clear();
            setState(prev => ({ ...prev, rejected: response?.rejected ?? [] }));

            console.log('[ReportEmployees] update error', response);
            helpers.failRequest(response, showToast, signOut);
        }

        showLoader(false);
    }

    const clear = () => setState(prev => ({ ...prev, fileDataStore: [], fileDataUpdate: [], rejected: [] }));

    const columns = [
        { label: 'Code', value: 'code' },
        { label: 'Name', value: 'name' },
        { label: 'Base rate', value: 'base_rate' },
        { label: 'Indicator', value: 'indicator' },
        { label: 'Email', value: 'email' },
        { label: 'Sede code', value: 'sede.code' },
        { label: 'Sede name', value: 'sede.name' },
        { label: 'Active', value: 'active' },
        { label: 'Is supplier', value: 'is_supplier' },
        { label: 'Payment', value: 'payment' },
        { label: 'Regular overtime', value: 'regular_overtime' },
        { label: 'Bonus payment', value: 'bonus_payment' },
        { label: 'Biweekly payment', value: 'biweekly_payment' },
        { label: 'CC1', value: 'cc1.code' },
        { label: 'CC2', value: 'cc2.code' },
        { label: 'CC3', value: 'cc3.code' },
        { label: 'Supervisor code', value: 'supervisor.code' },
        { label: 'Supervisor name', value: 'supervisor.name' },
    ];

    if (state.loading) return <Loader />;

    return (
        <div className={classes.root}>
            <ViewTitle title='Employees' />
            <Grid container>
                <Grid item xs={6} lg={3}>
                    <FormGroup>
                        <FormLabel>{'Cost center 1'}</FormLabel>
                        <Autocomplete
                            options={state.CC1}
                            value={state.filter.cc1}
                            getOptionLabel={option => option?.name ?? ''}
                            getOptionSelected={(option, value) => option.id === value.id}
                            onChange={(e, value) => handleChangeAutocomplete('cc1', value)}
                            renderInput={params => <TextField {...params} placeholder='CC1' />}
                        />
                    </FormGroup>
                </Grid>
                <Grid item xs={6} lg={3}>
                    <FormGroup>
                        <FormLabel>{'Cost center 2'}</FormLabel>
                        <Autocomplete
                            options={state.CC2}
                            value={state.filter.cc2}
                            getOptionLabel={option => option?.name ?? ''}
                            getOptionSelected={(option, value) => option.id === value.id}
                            onChange={(e, value) => handleChangeAutocomplete('cc2', value)}
                            renderInput={params => <TextField {...params} placeholder='CC2' />}
                        />
                    </FormGroup>
                </Grid>
                <Grid item xs={6} lg={3}>
                    <FormGroup>
                        <FormLabel>{'Cost center 3'}</FormLabel>
                        <Autocomplete
                            options={state.CC3}
                            value={state.filter.cc3}
                            getOptionLabel={option => option?.name ?? ''}
                            getOptionSelected={(option, value) => option.id === value.id}
                            onChange={(e, value) => handleChangeAutocomplete('cc3', value)}
                            renderInput={params => <TextField {...params} placeholder='CC3' />}
                        />
                    </FormGroup>
                </Grid>
            </Grid>

            <div className={classes.buttonsContainer}>
                <DownloadExcel fileName='employees' sheetName='Employees report' data={state.data} columns={columns} onClick={search} onReset={() => setState(prev => ({ ...prev, data: [] }))} />
            </div>

            <ViewTitle title='Add new employees' />
            <Grid container>
                <Grid item sm={6} md={4}>
                    <FormGroup>
                        <FormLabel>{'Upload file'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                autoComplete='off'
                                type='file'
                                name='fileDataStore'
                                onChange={handleFile}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>
                <Grid item sm={6} md={3} xl={1} className={classes.buttonContainer}>
                    <Button
                        variant='contained'
                        className={classes.search}
                        disabled={state.fileDataStore.length < 1}
                        style={{ width: '100%' }}
                        onClick={store}
                    >
                        {'Store data'}
                    </Button>
                </Grid>
            </Grid>

            <ViewTitle title='Update employees' />
            <Grid container>
                <Grid item sm={6} md={4}>
                    <FormGroup>
                        <FormLabel>{'Upload file'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                autoComplete='off'
                                type='file'
                                name='fileDataUpdate'
                                onChange={handleFile}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>
                <Grid item sm={6} md={3} xl={1} className={classes.buttonContainer}>
                    <Button
                        variant='contained'
                        className={classes.search}
                        disabled={state.fileDataUpdate.length < 1}
                        style={{ width: '100%' }}
                        onClick={update}
                    >
                        {'Update data'}
                    </Button>
                </Grid>
            </Grid>

            <Avoided open={open} onClose={() => setOpen(false)} data={state.rejected} />

        </div>
    );
}

export default ReportEmployees;